import { requestBidsAndSetTargeting } from 'client/utils/header-bidding/run-prebid-auction';

const registeredSlots = new Map();
const slotRegisteredSubscribers = new Set();

let pageTargeting = {};
let appliedTargetingKeys = new Set();
let initialLoadStarted = false;
let headerBiddingAllowed = false;
let servicesEnabled = false;

function getGoogletag() {
  if (typeof window === 'undefined') {
    return null;
  }

  window.googletag = window.googletag || {};
  window.googletag.cmd = window.googletag.cmd || [];
  return window.googletag;
}

function runGptCommand(callback) {
  const googletag = getGoogletag();
  if (!googletag) {
    return Promise.resolve(null);
  }

  return new Promise(resolve => {
    googletag.cmd.push(() => {
      try {
        resolve(callback(googletag));
      } catch {
        resolve(null);
      }
    });
  });
}

function applyPageTargeting(googletag) {
  const targeting = {};

  appliedTargetingKeys.forEach(key => {
    if (!Object.prototype.hasOwnProperty.call(pageTargeting, key) || pageTargeting[key] == null) {
      targeting[key] = null;
    }
  });

  const nextAppliedKeys = new Set();
  Object.entries(pageTargeting).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      targeting[key] = value;
      nextAppliedKeys.add(key);
    }
  });

  if (Object.keys(targeting).length) {
    googletag.setConfig({ targeting });
  }
  appliedTargetingKeys = nextAppliedKeys;
}

async function defineSlots(slots) {
  return runGptCommand(googletag => {
    const pubads = googletag.pubads();
    applyPageTargeting(googletag);

    const definedSlots = slots.reduce((result, slotConfig) => {
      const gptSlot = googletag
        .defineSlot(slotConfig.adUnitPath, slotConfig.sizes, slotConfig.slotId)
        ?.addService(pubads);

      if (!gptSlot) {
        return result;
      }

      const slotTargeting = Object.fromEntries(
        Object.entries(slotConfig.targetingArguments || {}).filter(([, value]) => value != null)
      );
      if (Object.keys(slotTargeting).length) {
        gptSlot.setConfig({ targeting: slotTargeting });
      }

      slotConfig.gptSlot = gptSlot; // eslint-disable-line no-param-reassign
      googletag.display(slotConfig.slotId);
      result.push(slotConfig);
      return result;
    }, []);

    if (!servicesEnabled) {
      googletag.enableServices();
      servicesEnabled = true;
    }

    return definedSlots;
  });
}

async function requestSlots(slots) {
  const definedSlots = await defineSlots(slots);
  if (!definedSlots?.length) {
    return;
  }

  if (headerBiddingAllowed) {
    const adUnits = definedSlots.map(slot => slot.prebidAdUnit).filter(Boolean);
    if (adUnits.length) {
      await requestBidsAndSetTargeting({
        adUnits,
        adUnitCodes: adUnits.map(adUnit => adUnit.code),
      });
    }
  }

  await runGptCommand(googletag => {
    applyPageTargeting(googletag);
    googletag.pubads().refresh(definedSlots.map(slot => slot.gptSlot));
  });
}

export function setPageTargeting(nextTargeting) {
  pageTargeting = nextTargeting || {};
  if (servicesEnabled) {
    runGptCommand(googletag => applyPageTargeting(googletag));
  }
}

export function registerGptSlotV2(slotConfig) {
  if (!registeredSlots.has(slotConfig.slotId)) {
    registeredSlots.set(slotConfig.slotId, { ...slotConfig, gptSlot: null });
    slotRegisteredSubscribers.forEach(callback => callback(slotConfig.slotId));

    if (initialLoadStarted) {
      requestSlots([registeredSlots.get(slotConfig.slotId)]);
    }
  }

  return registeredSlots.get(slotConfig.slotId);
}

export function unregisterGptSlotV2(slotId) {
  const slot = registeredSlots.get(slotId);
  registeredSlots.delete(slotId);

  if (slot?.gptSlot) {
    runGptCommand(googletag => googletag.destroySlots([slot.gptSlot]));
  }
}

export function subscribeSlotRegistered(callback) {
  slotRegisteredSubscribers.add(callback);
  return () => slotRegisteredSubscribers.delete(callback);
}

export function getRegisteredSlotCount() {
  return registeredSlots.size;
}

export function setHeaderBiddingAllowed(allowed = false) {
  headerBiddingAllowed = Boolean(allowed);
}

export function startInitialGptLoad({ isHeaderBiddingAllowed = false } = {}) {
  if (initialLoadStarted) {
    return Promise.resolve();
  }

  initialLoadStarted = true;
  headerBiddingAllowed = isHeaderBiddingAllowed;
  return requestSlots(Array.from(registeredSlots.values()));
}

export function refreshGptSlotsV2(...slotIds) {
  const slots = slotIds.length
    ? slotIds.map(slotId => registeredSlots.get(slotId)).filter(Boolean)
    : Array.from(registeredSlots.values()).filter(slot => !slot.shouldRefresh || slot.shouldRefresh());

  const definedSlots = slots.filter(slot => slot.gptSlot);
  if (!definedSlots.length) {
    return Promise.resolve();
  }

  const refreshSlots = async () => {
    if (headerBiddingAllowed) {
      const adUnitCodes = definedSlots.map(slot => slot.prebidAdUnit?.code).filter(Boolean);
      if (adUnitCodes.length) {
        await requestBidsAndSetTargeting({
          adUnitCodes,
          registerAdUnits: false,
        });
      }
    }

    return runGptCommand(googletag => {
      applyPageTargeting(googletag);
      googletag.pubads().refresh(definedSlots.map(slot => slot.gptSlot));
    });
  };

  return refreshSlots();
}

export function resetGptRuntimeV2ForTest() {
  registeredSlots.clear();
  slotRegisteredSubscribers.clear();
  pageTargeting = {};
  appliedTargetingKeys = new Set();
  initialLoadStarted = false;
  headerBiddingAllowed = false;
  servicesEnabled = false;
}
